'use client';

import { SSO_AUTH_CONFIG } from '@workspace/lib/auth/config';
import { PageLayout } from '@workspace/lib/components/organisms/PageLayout';
import { ProgressBar } from '@workspace/lib/components/organisms/ProgressBar';
import { useDeviceInfo } from '@workspace/lib/hooks/common/use-device-info';
import { isDevEnvironment } from '@workspace/lib/utils/app-env';
import { toAbsoluteHttpUrl } from '@workspace/lib/utils/to-absolute-http-url';

import { schema } from '@/business-logic/schemas/logo/logo-schema';
import { Logo } from '@/components/atoms/Logo';
import { CMS_URL } from '@/constants/external-urls';
import { usePageLayoutContainer } from '@/hooks/use-page-layout-container';
import { LocaleSwitch } from '@/temp/locale-switch/LocaleSwitch';
import { RouteProtectionSwitch } from '@/temp/route-protection-switch/RouteProtectionSwitch';

export function PageLayoutContainer({
  children,
}: {
  children: React.ReactNode;
}) {
  const { isMobileDevice } = useDeviceInfo();
  const {
    progressBarSchema,
    isProgressBarShown,
    isOfferPageGradientShown,
    isOfferPage,
    isThankYouPage,
    isDashboardPage,
    isBrokenJourneyPage,
    logoutButtonText,
    phoneNumber,
  } = usePageLayoutContainer();

  // After logout, send the user to the marketing site home (same as PL). The
  // pages that show the header logout are decided by
  // usePageLayoutContainer.logoutButtonText. Only pass a valid absolute
  // http(s) URL: when CMS_URL is unset ('/') or malformed, the Navbar falls
  // back to the app origin.
  const logoutReturnUrl = toAbsoluteHttpUrl(CMS_URL);

  return (
    <PageLayout
      logo={<Logo {...schema} />}
      isOfferOrThankYouPage={isOfferPage || isThankYouPage}
      isFullWidthPage={isDashboardPage}
      progressBar={
        isProgressBarShown && (
          <ProgressBar progressBarSchema={progressBarSchema} />
        )
      }
      isMobileDevice={isMobileDevice}
      isThankYouPageGradientLayerShown={isThankYouPage}
      isBrokenJourneyPage={isBrokenJourneyPage}
      isOfferPageGradientLayerShown={isOfferPageGradientShown}
      logoutButtonText={logoutButtonText}
      phoneNumber={phoneNumber}
      authConfig={SSO_AUTH_CONFIG}
      logoutReturnUrl={logoutReturnUrl}
    >
      {children}
      {/* TODO: for i18n testing. Remove in production */}
      {process.env.NEXT_PUBLIC_ENABLE_LOCALE_SWITCH === 'true' && (
        <div className='flex items-center py-2'>
          <p className='font-bold'>Change site locale: </p>
          <LocaleSwitch />
        </div>
      )}
      {isDevEnvironment && (
        <div className='flex items-center py-2'>
          <p className='font-bold'>Routes protection: </p>
          <RouteProtectionSwitch />
        </div>
      )}
    </PageLayout>
  );
}
